import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { GlowCard } from '../components/ui/GlowCard'
import { StatCard } from '../components/ui/StatCard'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/format'
import type { Proposal, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Drawer } from '../components/ui/Drawer'
import { useClientOptions, useProjectOptions } from '../hooks/useEntityOptions'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface ProposalStats {
  winRate: number
  avgDealSize: number
  pipelineValue: number
  conversionFunnel: { sent: number; approved: number }
}

/* Inline SVG icon components for stat cards */
function TrendingUpIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  )
}
function DollarIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  )
}
function PipelineIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}
function SendIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  )
}
function CheckIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  )
}

export default function Proposals() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [drawerOpen, setDrawerOpen] = useState(searchParams.get('action') === 'new')
  const initialClient = searchParams.get('client') || ''
  const [formData, setFormData] = useState({ client_slug: initialClient, project_slug: '', title: '', tax_rate: 0 })
  const [saving, setSaving] = useState(false)
  const [stats, setStats] = useState<ProposalStats | null>(null)
  const { options: clientOptions } = useClientOptions()
  const { options: projectOptions } = useProjectOptions(formData.client_slug || undefined)

  // Load analytics stats
  useEffect(() => {
    api.get<ProposalStats>('/proposals/analytics')
      .then(setStats)
      .catch(() => {})
  }, [])

  const loadProposals = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' })
      if (status) params.set('status', status)
      const result = await api.get<PaginatedResult<Proposal>>(`/proposals?${params}`)
      setProposals(result.data)
      setTotalPages(result.totalPages)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [page, status])

  useEffect(() => { loadProposals() }, [loadProposals])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const result = await api.post<{ slug?: string }>('/proposals', formData)
      setDrawerOpen(false)
      setFormData({ client_slug: '', project_slug: '', title: '', tax_rate: 0 })
      if (result?.slug) {
        navigate(`/proposals/${result.slug}`)
      } else {
        loadProposals()
      }
    } catch { /* error shown */ }
    finally { setSaving(false) }
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Proposals</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>New Proposal</Button>
      </div>

      {/* Analytics Stats Bar */}
      {stats && (
        <div style={{ display: 'flex', gap: `calc(14px * var(--density))`, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatCard icon={TrendingUpIcon} label="Win Rate" value={Math.round(stats.winRate)} suffix="%" delay={100} />
          <StatCard icon={DollarIcon} label="Avg Deal" value={Math.round(stats.avgDealSize)} prefix="$" delay={200} />
          <StatCard icon={PipelineIcon} label="Pipeline" value={Math.round(stats.pipelineValue)} prefix="$" delay={300} />
          <StatCard icon={SendIcon} label="Sent" value={stats.conversionFunnel?.sent ?? 0} delay={400} />
          <StatCard icon={CheckIcon} label="Closed" value={stats.conversionFunnel?.approved ?? 0} delay={500} />
        </div>
      )}

      <div className="mb-4">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1) }} className="input max-w-[160px]" style={{ fontSize: 13 }}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <TableSkeleton />
      ) : proposals.length === 0 ? (
        <EmptyState
          title="Ready to win some work?"
          description="Create your first proposal to get started."
          action={<Button onClick={() => setDrawerOpen(true)}>New Proposal</Button>}
        />
      ) : (
        <>
          <div className="hidden sm:block">
            <GlowCard style={{ padding: 0 }}>
              <table className="v3-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((p) => (
                    <tr key={p.id} onClick={() => navigate(`/proposals/${p.slug}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        <span style={{ color: 'var(--accent)', fontWeight: 500, fontSize: 13 }}>
                          {p.title}
                        </span>
                      </td>
                      <td><StatusBadge status={p.status} /></td>
                      <td className="capitalize" style={{ color: 'var(--text-2)', fontSize: 13 }}>{p.proposal_type.replace('_', ' ')}</td>
                      <td style={{ textAlign: 'right', fontWeight: 500, fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--text-1)' }}>{formatCurrency(p.total)}</td>
                      <td style={{ color: 'var(--text-3)', fontSize: 12, fontFamily: 'var(--mono)' }}>{new Date(p.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </GlowCard>
          </div>

          <div className="sm:hidden space-y-3">
            {proposals.map((p) => (
              <Link key={p.id} to={`/proposals/${p.slug}`} className="mobile-card block" style={{ textDecoration: 'none' }}>
                <div className="flex justify-between items-start gap-2">
                  <p className="min-w-0 truncate" style={{ fontSize: 14, fontWeight: 500, color: 'var(--accent)' }}>{p.title}</p>
                  <StatusBadge status={p.status} />
                </div>
                <div className="mobile-card-row mt-2">
                  <span className="capitalize" style={{ fontSize: 12, color: 'var(--text-2)' }}>{p.proposal_type.replace('_', ' ')}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', fontFamily: 'var(--mono)' }}>{formatCurrency(p.total)}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, fontFamily: 'var(--mono)' }}>
                  {new Date(p.created_at).toLocaleDateString()}
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost btn-sm" style={{ fontSize: 12 }}>Previous</button>
              <span style={{ fontSize: 13, color: 'var(--text-2)', fontFamily: 'var(--mono)' }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-ghost btn-sm" style={{ fontSize: 12 }}>Next</button>
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => { setDrawerOpen(false); if (searchParams.get('action')) setSearchParams({}, { replace: true }) }} title="New Proposal">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Client</label>
            <select
              value={formData.client_slug}
              onChange={(e) => setFormData({ ...formData, client_slug: e.target.value, project_slug: '' })}
              className="input"
            >
              <option value="">No client (assign later)</option>
              {clientOptions.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Project</label>
            <select
              value={formData.project_slug}
              onChange={(e) => setFormData({ ...formData, project_slug: e.target.value })}
              className="input"
              disabled={!formData.client_slug}
            >
              <option value="">None — auto-create from title</option>
              {projectOptions.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>Projects help organize multiple proposals for the same client</p>
          </div>
          <div>
            <label className="label">Title *</label>
            <input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input" required
            />
          </div>
          <div>
            <label className="label">Tax Rate (%)</label>
            <input
              type="number" step="0.01" value={formData.tax_rate}
              onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
              className="input"
            />
          </div>
          <Button type="submit" loading={saving} style={{ width: '100%' }}>
            Create Proposal
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
